import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PettyCashTab from '../components/PettyCashTab';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Users, Calendar, Clock, Wallet, FileText, CalendarDays, Download, BookOpen, User, Camera, Eye, EyeOff, Loader2 } from 'lucide-react';
import DepartmentPoliciesNav from '../components/DepartmentPoliciesNav';

const API_URL = '';

export default function EmployeeHub({ user, onLogout, theme, onThemeToggle }) {
  const [leaves, setLeaves] = useState([]);
  const [otApplications, setOtApplications] = useState([]);
  const [leaveCredits, setLeaveCredits] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [itrDocuments, setItrDocuments] = useState([]);
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'VL', start_date: '', end_date: '', reason: '' });
  const [otForm, setOtForm] = useState({ date: '', hours: '', justification: '' });
  const [showAllOt, setShowAllOt] = useState(false);
  
  // Profile state
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ full_name: '', phone_number: '' });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    axios.get(`${API_URL}/api/leave/my`).then(res => setLeaves(res.data));
    axios.get(`${API_URL}/api/ot/my`).then(res => setOtApplications(res.data));
    axios.get(`${API_URL}/api/leave-credits`).then(res => setLeaveCredits(res.data)).catch(err => console.error(err));
    axios.get(`${API_URL}/api/payslips`).then(res => setPayslips(res.data)).catch(err => console.error(err));
    axios.get(`${API_URL}/api/itr`).then(res => setItrDocuments(res.data)).catch(err => console.error(err));
    loadProfile();
  };

  const loadProfile = () => {
    axios.get(`${API_URL}/api/profile`)
      .then(res => {
        setProfile(res.data);
        setProfileForm({
          full_name: res.data.full_name || '',
          phone_number: res.data.phone_number || ''
        });
      })
      .catch(err => console.error('Error loading profile:', err));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!profileForm.full_name.trim()) {
      toast.error('Name is required');
      return;
    }
    setProfileLoading(true);
    try {
      const formData = new FormData();
      formData.append('full_name', profileForm.full_name);
      formData.append('phone_number', profileForm.phone_number || '');
      
      const res = await axios.put(`${API_URL}/api/profile`, formData);
      toast.success('Profile updated successfully');
      setProfile(res.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      const formData = new FormData();
      formData.append('current_password', passwordForm.current_password);
      formData.append('new_password', passwordForm.new_password);
      
      await axios.put(`${API_URL}/api/profile/password`, formData);
      toast.success('Password updated successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, GIF, and WebP images are allowed');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    
    setPhotoLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API_URL}/api/profile/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile photo updated');
      setProfile(prev => ({ ...prev, picture: res.data.picture }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setPhotoLoading(false);
    }
  };

  const handlePhotoDelete = async () => {
    if (!window.confirm('Are you sure you want to delete your profile photo?')) return;
    
    setPhotoLoading(true);
    try {
      await axios.delete(`${API_URL}/api/profile/photo`);
      toast.success('Profile photo deleted');
      setProfile(prev => ({ ...prev, picture: null }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete photo');
    } finally {
      setPhotoLoading(false);
    }
  };

  const getProfilePhotoUrl = () => {
    if (!profile?.picture) return null;
    if (profile.picture.startsWith('http')) return profile.picture;
    return `${API_URL}${profile.picture}`;
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1);
  };

  const getAvailableCredits = (leaveType) => {
    if (!leaveCredits) return 0;
    switch (leaveType) {
      case 'VL': return leaveCredits.vacation_remaining || 0;
      case 'SL': return leaveCredits.sick_remaining || 0;
      case 'Emergency': return leaveCredits.emergency_remaining || 0;
      default: return 0;
    }
  };

  const daysRequested = calculateDays(leaveForm.start_date, leaveForm.end_date);
  const availableCredits = getAvailableCredits(leaveForm.leave_type);
  const insufficientCredits = daysRequested > availableCredits;

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    
    if (insufficientCredits) {
      toast.error(`Insufficient ${leaveForm.leave_type} credits. You have ${availableCredits.toFixed(1)} days remaining.`);
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/leave`, leaveForm, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      toast.success('Leave application submitted!');
      setLeaveForm({ leave_type: 'VL', start_date: '', end_date: '', reason: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to submit leave application');
    }
  };

  const handleOtSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/ot`, otForm, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      toast.success('OT application submitted!');
      setOtForm({ date: '', hours: '', justification: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to submit OT application');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} theme={theme} onThemeToggle={onThemeToggle}>
      <div className="p-4 sm:p-8 space-y-6 sm:space-y-8" data-testid="employee-hub-page">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/20 rounded-md flex items-center justify-center">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-foreground">Employee Hub</h1>
            <p className="text-xs sm:text-base text-muted-foreground">Leave applications, overtime requests, pay vault, and policies</p>
          </div>
        </div>

        <Tabs scope="employee-hub" defaultValue="leave" className="space-y-6">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 pb-2">
            <TabsList className="bg-muted/50 flex-wrap h-auto gap-1 justify-start w-full sm:w-auto">
              <TabsTrigger value="leave" data-testid="tab-leave" className="whitespace-nowrap text-xs sm:text-sm">Leave</TabsTrigger>
              <TabsTrigger value="ot" data-testid="tab-ot" className="whitespace-nowrap text-xs sm:text-sm">OT</TabsTrigger>
              <TabsTrigger value="pay" data-testid="tab-pay" className="whitespace-nowrap text-xs sm:text-sm">Pay Vault</TabsTrigger>
              <TabsTrigger value="policies" data-testid="tab-policies" className="whitespace-nowrap text-xs sm:text-sm">Policies</TabsTrigger>
              <TabsTrigger value="profile" data-testid="tab-profile" className="whitespace-nowrap text-xs sm:text-sm">Profile</TabsTrigger>
              <TabsTrigger value="petty-cash" data-testid="tab-petty-cash" className="whitespace-nowrap text-xs sm:text-sm">Petty Cash</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="leave" className="space-y-6">
            {leaveCredits && (
              <Card className="glass-effect border-border/40" data-testid="leave-credits-summary">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <CalendarDays className="w-5 h-5 text-blue-500" />
                    <CardTitle className="text-lg">Your Leave Credits ({leaveCredits.year})</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className={`text-center p-3 rounded-lg border ${leaveForm.leave_type === 'VL' ? 'bg-blue-500/20 border-blue-500' : 'bg-blue-500/10 border-blue-500/30'}`}>
                      <p className="text-xl font-bold text-blue-500">{leaveCredits.vacation_remaining?.toFixed(1)}</p>
                      <p className="text-xs text-muted-foreground">Vacation (VL)</p>
                    </div>
                    <div className={`text-center p-3 rounded-lg border ${leaveForm.leave_type === 'SL' ? 'bg-green-500/20 border-green-500' : 'bg-green-500/10 border-green-500/30'}`}>
                      <p className="text-xl font-bold text-green-500">{leaveCredits.sick_remaining?.toFixed(1)}</p>
                      <p className="text-xs text-muted-foreground">Sick (SL)</p>
                    </div>
                    <div className={`text-center p-3 rounded-lg border ${leaveForm.leave_type === 'Emergency' ? 'bg-orange-500/20 border-orange-500' : 'bg-orange-500/10 border-orange-500/30'}`}>
                      <p className="text-xl font-bold text-orange-500">{leaveCredits.emergency_remaining?.toFixed(1)}</p>
                      <p className="text-xs text-muted-foreground">Emergency</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="glass-effect border-border/40" data-testid="leave-form-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-primary" />
                  <CardTitle>Apply for Leave</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLeaveSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="leave_type">Leave Type</Label>
                    <select
                      id="leave_type"
                      data-testid="select-leave-type"
                      className="flex h-11 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus:ring-2 focus:ring-primary/20"
                      value={leaveForm.leave_type}
                      onChange={(e) => setLeaveForm({ ...leaveForm, leave_type: e.target.value })}
                    >
                      <option value="VL">Vacation Leave (VL) - {leaveCredits?.vacation_remaining?.toFixed(1) || 0} days left</option>
                      <option value="SL">Sick Leave (SL) - {leaveCredits?.sick_remaining?.toFixed(1) || 0} days left</option>
                      <option value="Emergency">Emergency Leave - {leaveCredits?.emergency_remaining?.toFixed(1) || 0} days left</option>
                    </select>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start_date">Start Date</Label>
                      <Input
                        id="start_date"
                        type="date"
                        data-testid="input-start-date"
                        value={leaveForm.start_date}
                        onChange={(e) => setLeaveForm({ ...leaveForm, start_date: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_date">End Date</Label>
                      <Input
                        id="end_date"
                        type="date"
                        data-testid="input-end-date"
                        value={leaveForm.end_date}
                        onChange={(e) => setLeaveForm({ ...leaveForm, end_date: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  {leaveForm.start_date && leaveForm.end_date && (
                    <div className={`p-3 rounded-md ${insufficientCredits ? 'bg-red-500/10 border border-red-500/30' : 'bg-muted/30'}`}>
                      <p className={`text-sm ${insufficientCredits ? 'text-red-500' : 'text-foreground'}`}>
                        Days requested: <span className="font-bold">{daysRequested}</span> | Available: <span className="font-bold">{availableCredits.toFixed(1)}</span>
                        {insufficientCredits && <span className="block mt-1 text-red-400">Insufficient leave credits</span>}
                      </p>
                    </div>
                  )}
                  <div>
                    <Label htmlFor="reason">Reason</Label>
                    <Textarea
                      id="reason"
                      data-testid="input-leave-reason"
                      value={leaveForm.reason}
                      onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                      placeholder="Reason for leave..."
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    data-testid="btn-submit-leave"
                    disabled={insufficientCredits}
                    className={insufficientCredits ? 'opacity-50 cursor-not-allowed' : ''}
                  >
                    Submit Application
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="glass-effect border-border/40" data-testid="leave-list-card">
              <CardHeader>
                <CardTitle>Your Leave Applications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {leaves.map((leave) => (
                    <div key={leave.id} className="p-4 bg-muted/30 rounded-md" data-testid={`leave-${leave.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-foreground">{leave.leave_type} Leave</p>
                          <p className="text-sm text-muted-foreground">{leave.start_date} to {leave.end_date}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded capitalize ${
                          leave.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                          leave.status === 'rejected' ? 'bg-red-500/20 text-red-500' :
                          'bg-yellow-500/20 text-yellow-500'
                        }`}>{leave.status}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{leave.reason}</p>
                    </div>
                  ))}
                  {leaves.length === 0 && <p className="text-muted-foreground text-sm">No leave applications yet</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ot" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="ot-form-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-primary" />
                  <CardTitle>Apply for Overtime</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleOtSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ot_date">Date</Label>
                      <Input
                        id="ot_date"
                        type="date"
                        data-testid="input-ot-date"
                        value={otForm.date}
                        onChange={(e) => setOtForm({ ...otForm, date: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="ot_hours">Hours</Label>
                      <Input
                        id="ot_hours"
                        type="number"
                        step="0.5"
                        data-testid="input-ot-hours"
                        value={otForm.hours}
                        onChange={(e) => setOtForm({ ...otForm, hours: e.target.value })}
                        placeholder="2"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="justification">Justification</Label>
                    <Textarea
                      id="justification"
                      data-testid="input-ot-justification"
                      value={otForm.justification}
                      onChange={(e) => setOtForm({ ...otForm, justification: e.target.value })}
                      placeholder="Reason for overtime (e.g., Deadline for CD Set)..."
                      required
                    />
                  </div>
                  <Button type="submit" data-testid="btn-submit-ot">Submit Application</Button>
                </form>
              </CardContent>
            </Card>

            <Card className="glass-effect border-border/40" data-testid="ot-list-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Your OT Applications</CardTitle>
                  {otApplications.length > 5 && (
                    <button onClick={() => setShowAllOt(v => !v)} className="text-xs text-primary hover:underline" data-testid="btn-toggle-all-my-ot">
                      {showAllOt ? 'Show recent' : `Show all ${otApplications.length} previous`}
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(showAllOt ? otApplications : otApplications.slice(0, 5)).map((ot) => (
                    <div key={ot.id} className="p-4 bg-muted/30 rounded-md" data-testid={`ot-${ot.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-foreground">{ot.date} - {ot.hours} hours</p>
                          <p className="text-sm text-muted-foreground">{ot.justification}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded capitalize ${
                          ot.status === 'approved' ? 'bg-green-500/20 text-green-500' :
                          ot.status === 'rejected' ? 'bg-red-500/20 text-red-500' :
                          'bg-yellow-500/20 text-yellow-500'
                        }`}>{ot.status}</span>
                      </div>
                      {ot.status === 'approved' && ot.ot_code && (
                        <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-md">
                          <p className="text-xs text-muted-foreground mb-1">OT Code (use in DTR Overtime section):</p>
                          <p className="text-lg font-bold text-green-500 tracking-wider" data-testid={`ot-code-${ot.id}`}>{ot.ot_code}</p>
                        </div>
                      )}
                    </div>
                  ))}
                  {otApplications.length === 0 && <p className="text-muted-foreground text-sm">No OT applications yet</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pay">
            <Card className="glass-effect border-border/40" data-testid="pay-vault-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Wallet className="w-5 h-5 text-emerald-500" />
                  <CardTitle>My Pay Vault</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-6">
                  Secure archive for your payslips and tax documents (BIR Form 2316). Download files in PDF format.
                </p>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Payslips Section */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-emerald-500" />
                      My Payslips
                    </h3>
                    {payslips.length > 0 ? (
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {payslips.map((payslip) => (
                          <div 
                            key={payslip.id} 
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-md hover-lift"
                            data-testid={`my-payslip-${payslip.id}`}
                          >
                            <div>
                              <p className="font-medium text-foreground">{payslip.month_name} {payslip.year}</p>
                              <p className="text-xs text-muted-foreground">Payslip</p>
                            </div>
                            <a 
                              href={`${API_URL}${payslip.file_url}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-sm text-emerald-500 hover:text-emerald-400"
                              data-testid={`btn-download-payslip-${payslip.id}`}
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </a>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-6 bg-muted/20 rounded-md text-center">
                        <p className="text-muted-foreground text-sm">No payslips available yet</p>
                        <p className="text-xs text-muted-foreground mt-1">Contact HR if you expect payslips here</p>
                      </div>
                    )}
                  </div>

                  {/* ITR Documents Section */}
                  <div>
                    <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-500" />
                      My ITR Documents (BIR Form 2316)
                    </h3>
                    {itrDocuments.length > 0 ? (
                      <div className="space-y-2 max-h-[400px] overflow-y-auto">
                        {itrDocuments.map((itr) => (
                          <div 
                            key={itr.id} 
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-md hover-lift"
                            data-testid={`my-itr-${itr.id}`}
                          >
                            <div>
                              <p className="font-medium text-foreground">ITR {itr.year}</p>
                              <p className="text-xs text-muted-foreground">BIR Form 2316</p>
                            </div>
                            <a 
                              href={`${API_URL}${itr.file_url}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-sm text-blue-500 hover:text-blue-400"
                              data-testid={`btn-download-itr-${itr.id}`}
                            >
                              <Download className="w-4 h-4" />
                              Download
                            </a>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-6 bg-muted/20 rounded-md text-center">
                        <p className="text-muted-foreground text-sm">No ITR documents available yet</p>
                        <p className="text-xs text-muted-foreground mt-1">ITR documents are typically uploaded annually</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="policies">
            <Card className="glass-effect border-border/40" data-testid="policies-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-indigo-500" />
                  <CardTitle>Company Policies</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-6">
                  Select a department to view their memos, policies, guidelines, and protocols.
                </p>
                
                <DepartmentPoliciesNav />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="profile" className="space-y-6">
            <Card className="glass-effect border-border/40" data-testid="profile-photo-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Camera className="w-5 h-5 text-primary" />
                  <CardTitle>Profile Photo</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full bg-muted/50 border-2 border-border overflow-hidden flex items-center justify-center">
                      {getProfilePhotoUrl() ? (
                        <img 
                          src={getProfilePhotoUrl()} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                          data-testid="profile-photo-preview"
                        />
                      ) : (
                        <User className="w-10 h-10 text-muted-foreground" />
                      )}
                    </div>
                    {photoLoading && (
                      <div className="absolute inset-0 flex items-center justify-center bg-background/50 rounded-full">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="photo-upload"
                      data-testid="input-photo-upload"
                    />
                    <label htmlFor="photo-upload">
                      <Button 
                        type="button" 
                        variant="outline" 
                        className="cursor-pointer" 
                        asChild
                        disabled={photoLoading}
                      >
                        <span data-testid="btn-upload-photo">
                          <Camera className="w-4 h-4 mr-2" />
                          {profile?.picture ? 'Change Photo' : 'Upload Photo'}
                        </span>
                      </Button>
                    </label>
                    {profile?.picture && profile.picture.startsWith('/uploads/') && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={handlePhotoDelete}
                        disabled={photoLoading}
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                        data-testid="btn-delete-photo"
                      >
                        Remove Photo
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">
                      JPG, PNG, GIF or WebP. Max 5MB.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-effect border-border/40" data-testid="profile-info-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <User className="w-5 h-5 text-primary" />
                  <CardTitle>Personal Information</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="profile_email">Email Address</Label>
                    <Input
                      id="profile_email"
                      type="email"
                      value={profile?.email || ''}
                      disabled
                      className="bg-muted/30 cursor-not-allowed"
                      data-testid="input-profile-email"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
                  </div>
                  <div>
                    <Label htmlFor="profile_name">Full Name</Label>
                    <Input
                      id="profile_name"
                      type="text"
                      value={profileForm.full_name}
                      onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                      placeholder="Your full name"
                      required
                      data-testid="input-profile-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="profile_phone">Contact Number</Label>
                    <Input
                      id="profile_phone"
                      type="tel"
                      value={profileForm.phone_number}
                      onChange={(e) => setProfileForm({ ...profileForm, phone_number: e.target.value })}
                      placeholder="+63 9XX XXX XXXX"
                      data-testid="input-profile-phone"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div>
                      <Label className="text-muted-foreground">Role</Label>
                      <p className="text-foreground capitalize">{profile?.role || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Department</Label>
                      <p className="text-foreground">{profile?.department || '-'}</p>
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    disabled={profileLoading}
                    data-testid="btn-save-profile"
                  >
                    {profileLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card className="glass-effect border-border/40" data-testid="password-card">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-amber-500" />
                  <CardTitle>Change Password</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {profile?.picture?.startsWith('http') && !profile?.password ? (
                  <div className="p-4 bg-muted/30 rounded-md">
                    <p className="text-muted-foreground text-sm">
                      Password management is not available for Google-authenticated accounts.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handlePasswordSubmit} className="space-y-4">
                    <div>
                      <Label htmlFor="current_password">Current Password</Label>
                      <div className="relative">
                        <Input
                          id="current_password"
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={passwordForm.current_password}
                          onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                          placeholder="Enter current password"
                          required
                          data-testid="input-current-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          data-testid="btn-toggle-current-password"
                        >
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="new_password">New Password</Label>
                      <div className="relative">
                        <Input
                          id="new_password"
                          type={showNewPassword ? 'text' : 'password'}
                          value={passwordForm.new_password}
                          onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                          placeholder="Enter new password (min 6 characters)"
                          required
                          data-testid="input-new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          data-testid="btn-toggle-new-password"
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="confirm_password">Confirm New Password</Label>
                      <Input
                        id="confirm_password"
                        type={showNewPassword ? 'text' : 'password'}
                        value={passwordForm.confirm_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                        placeholder="Confirm new password"
                        required
                        data-testid="input-confirm-password"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      variant="outline"
                      disabled={passwordLoading}
                      data-testid="btn-change-password"
                    >
                      {passwordLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        'Change Password'
                      )}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="petty-cash" className="space-y-6">
            <PettyCashTab />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
